import { ClockCircleOutlined } from '@ant-design/icons'
import useMediaQuery from '@cannect/hooks/useMediaQuery'
import { BRANDS_WITH_REQUIRED_FREIGHT, MOBILE_WIDTH } from '@cannect/utils/constants'
import { canApplyPixDiscount } from '@cannect/utils/pixDiscount'
import { getPayableAmount } from '@cannect/utils/reshipping'
import LoadingContainer from 'components/LoadingContainer'
import { AnimatePresence, motion } from 'framer-motion'
import { Order, PaymentMethod } from 'hooks/useCartCheckout'
import { usePrepareCart } from 'hooks/usePrepareCart'
import { CheckCircle } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { RiErrorWarningFill } from 'react-icons/ri'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import StatusModal from '../StatusModal'
import { CountDown } from './CountDown'
import * as Style from './styles'

interface Props {
  order?: Order
  payment?: PaymentMethod
  pixInfo?: string
  loadingContainer: boolean
  isCardPix?: boolean
  pixAmount?: number
  onProcessingChange?: (isProcessing: boolean) => void
}

interface PixTransaction {
  qr_code: string
  qr_code_url: string
  expires_at: string
  charge_id: string
  order_code: string
}

interface ChargeStatus {
  success: boolean
  order_paid: boolean
  order: {
    id: number
    uuid: string
    amount: string
    originCheckout: string
  }
  charge: {
    status: string
  }
}

export function Pix({ loadingContainer, isCardPix = false, pixAmount = 0, onProcessingChange }: Props) {
  const [lineIsCopied, setLineIsCopied] = useState(false)
  const [error, setError] = useState(false)
  const isMobile = useMediaQuery(MOBILE_WIDTH)

  const [, setCopyClipboard] = useState<string>('')
  const [pixTransaction, setPixTransaction] = useState<PixTransaction | null>(null)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<string>('pending')
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [allowRedirect, setAllowRedirect] = useState(false)
  const [errorDetails, setErrorDetails] = useState<{
    status?: string
    acquirer_message?: string
  } | null>(null)
  const { lastOrderCreated, setOrderId, clearOrderData } = usePrepareCart()
  const applyPixDiscount = canApplyPixDiscount(lastOrderCreated)
  const pixDiscountPercentage = Number(import.meta.env.VITE_APP_PIX_DISCOUNT_PERCENTAGE)
  // Reenvio cobra a taxa combinada com o paciente, não o total do pedido.
  const payableAmount = getPayableAmount(lastOrderCreated)
  const location = useLocation()
  const historyStateRef = useRef(false)

  // Extract order ID from URL path
  const orderIdFromUrl = location.pathname.split('/').pop()
  const cartId = new URLSearchParams(location.search).get('cart_id')

  const history = useHistory()

  const isAllSupplements = lastOrderCreated?.items.every(
    (item: any) =>
      item?.product?.class_id === 6 && !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )
  const originCheck = ['acolhimento-cannect', 'B2C', 'COD B', 'Recipe', 'COD C', 'app-cannect', 'agente-autonomo']

  const copyByClipboard = (text: string) => {
    const fallbackCopy = (t: string) => {
      try {
        const textarea = document.createElement('textarea')
        textarea.value = t
        textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      } catch {
        // sem suporte a cópia
      }
    }

    const onCopied = () => {
      setCopyClipboard(text)
      setLineIsCopied(true)
      toast.success('Código PIX copiado para a área de transferência')
      setTimeout(() => setLineIsCopied(false), 3000)
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(onCopied)
        .catch(() => {
          fallbackCopy(text)
          onCopied()
        })
    } else {
      fallbackCopy(text)
      onCopied()
    }
  }

  const checkPaymentStatus = useCallback(
    async (orderId: string, chargeId: string) => {
      try {
        const response = await api.get<ChargeStatus>(`payments/charge/${orderId}/${chargeId}`)
        if (response.data.success) {
          setPaymentStatus(response.data.charge.status)
          if (response.data.charge.status === 'paid') {
            setPaymentSuccess(true)
            setAllowRedirect(true)
            localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART_ID')
            setOrderId(null)
            clearOrderData()
            // Wait 3 seconds before redirecting
            console.log('CAI AQUI PAGAMENTO EM PIX')
            console.log('response.data.order.uuid', response.data.order.uuid)
            console.log('allowRedirect setado como:', true)

            setTimeout(() => {
              setIsProcessingPayment(false)
              setIsLoading(false)
              setAllowRedirect(true)

              const skipDocuments = isAllSupplements || !!cartId
              const redirectUrl = skipDocuments
                ? `/detalhes-do-pedido/${response.data.order.id}`
                : `/enviar-receita/${response.data.order.id}`

              try {
                history.push(redirectUrl)
              } catch {
                window.location.href = redirectUrl
              }
            }, 3000)
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error)
      }
    },
    [history, setOrderId, clearOrderData, isAllSupplements, cartId]
  )

  const applyPaymentDiscount = (amount: number) => {
    if (applyPixDiscount) {
      const discount = amount * (pixDiscountPercentage / 100)
      return amount - discount
    }
    return amount
  }

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>

    if (isConfirmed && pixTransaction) {
      intervalId = setInterval(() => {
        const orderId = pixTransaction.order_code || orderIdFromUrl || ''
        checkPaymentStatus(orderId, pixTransaction.charge_id)
      }, 5000)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [isConfirmed, pixTransaction, checkPaymentStatus, orderIdFromUrl])

  useEffect(() => {
    onProcessingChange?.(isProcessingPayment || isLoading)
  }, [isProcessingPayment, isLoading, onProcessingChange])

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if ((isProcessingPayment || isLoading) && !allowRedirect) {
        event.preventDefault()
      }
    }

    const handlePopState = (event: PopStateEvent) => {
      if ((isProcessingPayment || isLoading) && !allowRedirect) {
        window.history.pushState(null, document.title, window.location.href)
        toast.warning('Por favor, aguarde o processamento do pagamento')
      }
    }

    // Se allowRedirect for true, não impedir navegação
    if (allowRedirect) {
      console.log('allowRedirect é true, permitindo navegação')
      if (historyStateRef.current) {
        historyStateRef.current = false
        window.removeEventListener('popstate', handlePopState)
      }
    } else if ((isProcessingPayment || isLoading) && !allowRedirect && !historyStateRef.current) {
      window.history.pushState(null, document.title, window.location.href)
      historyStateRef.current = true
      window.addEventListener('popstate', handlePopState)
    } else if (!isProcessingPayment && !isLoading && !allowRedirect) {
      if (historyStateRef.current) {
        historyStateRef.current = false
        window.removeEventListener('popstate', handlePopState)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (historyStateRef.current) {
        window.removeEventListener('popstate', handlePopState)
      }
    }
  }, [isProcessingPayment, isLoading, allowRedirect])

  const handleFinishOrder = async () => {
    try {
      setIsLoading(true)
      setIsProcessingPayment(true)

      const amount = applyPixDiscount && !isCardPix ? applyPaymentDiscount(payableAmount) : payableAmount
      const payload = {
        order_id: lastOrderCreated.id,
        payments: [
          {
            paymentMethod: { type: 'pix' },
            amount: Math.round(amount * 100)
          }
        ]
      }

      const response = await api.post('/payments/charges', payload)
      if (response.data.success && response.data.responses && response.data.responses.length > 0) {
        const paymentResponse = response.data.responses[0]
        if (paymentResponse.last_transaction && !paymentResponse.last_transaction.success) {
          setErrorDetails({
            status: 'Erro ao gerar PIX',
            acquirer_message: 'Erro ao processar pagamento'
          })
          throw new Error('Payment transaction was not successful')
        } else if (paymentResponse.last_transaction && paymentResponse.last_transaction.success) {
          const transaction = paymentResponse.last_transaction
          const chargeId = paymentResponse.id
          const orderCode = paymentResponse.code
          setPixTransaction({
            qr_code: transaction.qr_code,
            qr_code_url: transaction.qr_code_url,
            expires_at: transaction.expires_at,
            charge_id: chargeId,
            order_code: orderCode
          })
          setIsConfirmed(true)
          setIsExpired(false)

          const documents = response.data?.documents

          const documentList = documents?.reduce(
            (acc, doc) => {
              acc[doc.type] = doc
              return acc
            },
            {} as Record<string, any>
          )

          // if (!isAllSupplements) {
          //   const validateAnvisa = documentList?.ANVISA !== undefined || documentList?.PROCURACAO !== undefined

          //   if (originCheck.some((origin) => origin === lastOrderCreated?.originCheckout) && !validateAnvisa) {
          //     const validator = await api.get(`anvisaAuth/order/${lastOrderCreated.id}`)

          //     if (validator?.data?.success && validator?.data?.message === 'waitingDocs') {
          //       const response = await api.post('anvisaAuth/launchAnvisa', {
          //         order_id: lastOrderCreated.id
          //       })
          //     } else if (validator?.data?.success && validator?.data?.message === 'inexist') {
          //       const response = await api.post('anvisaAuth/clickAutomation', {
          //         order_id: lastOrderCreated.id
          //       })
          //     }
          //   }
          // }
        } else {
          throw new Error('Error processing PIX payment')
        }
      } else {
        setErrorDetails({
          status: 'Erro ao gerar PIX',
          acquirer_message: 'Erro ao processar pagamento'
        })
        throw new Error('Error processing PIX payment')
      }
    } catch (error) {
      console.error('Error processing PIX payment:', error)
      setError(true)
      setIsProcessingPayment(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleExpiration = () => {
    setIsExpired(true)
    setIsProcessingPayment(false)
  }

  if (loadingContainer) {
    return <LoadingContainer loading />
  }

  if (isCardPix) {
    return (
      <Style.Container isMobile={isMobile}>
        <Style.ConfirmationCard>
          <Style.CardHeader>
            <h2>Pagamento Parcial via PIX</h2>
          </Style.CardHeader>
          <Style.CardContent>
            <Style.OrderDetails>
              <div className="detail-row">
                <span>Valor a pagar via PIX:</span>
                <div>
                  <strong>
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(pixAmount || 0)}
                  </strong>
                </div>
              </div>
            </Style.OrderDetails>
            <Style.InfoText>
              <p>
                Após preencher os dados do cartão de crédito, clique em &quot;Finalizar pedido&quot; para gerar o QR Code
                PIX.
              </p>
              <p>O pagamento via PIX deve ser realizado em até 30 minutos após a geração do QR Code.</p>
            </Style.InfoText>
          </Style.CardContent>
        </Style.ConfirmationCard>
        <StatusModal
          onClickButton={() => {
            setError(false)
            setErrorDetails(null)
          }}
          variant="error"
          icon={<RiErrorWarningFill />}
          textButton="Tentar Novamente"
          isOpen={error}
          onClose={() => {
            setError(false)
            setErrorDetails(null)
          }}
          title="Houve um erro ao processar seu pagamento"
          text={
            errorDetails ? (
              <div className="error-group">
                <h4>Erro no PIX</h4>
                <div className="detail-item">
                  <span className="label">Status:</span>
                  <span className="value">{errorDetails.status}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Mensagem:</span>
                  <span className="value">{errorDetails.acquirer_message}</span>
                </div>
              </div>
            ) : (
              'Por favor, revise seus dados e tente novamente'
            )
          }
        />
      </Style.Container>
    )
  }

  if (!isConfirmed) {
    return (
      <Style.Container isMobile={isMobile}>
        <Style.ConfirmationCard>
          <Style.CardHeader>
            <h2>Confirmação de Pagamento via PIX</h2>
          </Style.CardHeader>
          <Style.CardContent>
            <Style.OrderDetails>
              <div className="detail-row">
                <span>Valor total:</span>
                <div>
                  {applyPixDiscount ? (
                    <div className="discount-container">
                      <div className="discount-container-label">
                        <span className="discount-label">-{pixDiscountPercentage}%</span>
                        <span className="discount-value">
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(payableAmount)}
                        </span>
                      </div>
                      <strong>
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(applyPaymentDiscount(payableAmount))}
                      </strong>
                    </div>
                  ) : (
                    <strong>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(applyPaymentDiscount(payableAmount))}
                    </strong>
                  )}
                </div>
              </div>
              <div className="detail-row">
                <span>Método de pagamento:</span>
                <strong>PIX</strong>
              </div>
              <div className="detail-row">
                <span>Tempo de expiração:</span>
                <strong>2 horas</strong>
              </div>
            </Style.OrderDetails>
            <Style.InfoText>
              <p>Ao confirmar, você receberá um QR Code PIX para realizar o pagamento.</p>
              <p>O pagamento deve ser realizado em até 2 horas para garantir a confirmação do seu pedido.</p>
            </Style.InfoText>
            <Style.ButtonContainer>
              <Style.Button onClick={handleFinishOrder} disabled={isLoading}>
                {isLoading ? 'Gerando QR Code...' : 'Gerar QR Code PIX'}
              </Style.Button>
            </Style.ButtonContainer>
          </Style.CardContent>
        </Style.ConfirmationCard>
        <StatusModal
          onClickButton={() => {
            setError(false)
            setErrorDetails(null)
          }}
          variant="error"
          icon={<RiErrorWarningFill />}
          textButton="Tentar Novamente"
          isOpen={error}
          onClose={() => {
            setError(false)
            setErrorDetails(null)
          }}
          title="Houve um erro ao processar seu pagamento"
          text={
            errorDetails ? (
              <div className="error-group">
                <h4>Erro no PIX</h4>
                <div className="detail-item">
                  <span className="label">Status:</span>
                  <span className="value">{errorDetails.status}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Mensagem:</span>
                  <span className="value">{errorDetails.acquirer_message}</span>
                </div>
              </div>
            ) : (
              'Por favor, revise seus dados e tente novamente'
            )
          }
        />
      </Style.Container>
    )
  }

  return (
    <Style.Container isMobile={isMobile}>
      <Style.PixCard>
        <Style.CardHeader>
          <h2>Pagamento via PIX</h2>
        </Style.CardHeader>
        <Style.CardContent>
          {isConfirmed && pixTransaction ? (
            <AnimatePresence>
              {paymentSuccess ? (
                <Style.SuccessMessage
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}>
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                    <CheckCircle size={48} className="success-icon" />
                  </motion.div>
                  <h3>Pagamento confirmado!</h3>
                  <p>Você será redirecionado em instantes...</p>
                </Style.SuccessMessage>
              ) : (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <Style.CountDownContainer>
                    <ClockCircleOutlined style={{ fontSize: '24px', paddingRight: '10px' }} />
                    <Style.CountDownContent>
                      <CountDown expiresAt={pixTransaction?.expires_at} onExpire={handleExpiration} />
                      <p>para expirar</p>
                    </Style.CountDownContent>
                  </Style.CountDownContainer>
                  <Style.QrCodeContainer>
                    <img src={pixTransaction?.qr_code_url} alt="QR Code PIX" />
                    <Style.QrCodeinfos>
                      <p>1. Abra o app do seu banco ou instituição financeira.</p>
                      <p>2. Escolha a opção de pagamento via PIX.</p>
                      <p>3. Escaneie o QR Code ou copie e cole o código PIX.</p>
                      <p>4. Confirme as informações e finalize o pagamento.</p>
                      <p>5. Após a confirmação, você será redirecionado para a página de resumo do pedido.</p>
                    </Style.QrCodeinfos>
                  </Style.QrCodeContainer>

                  {lineIsCopied ? (
                    <Style.ButtonCopied>✓ Código copiado!</Style.ButtonCopied>
                  ) : (
                    <Style.ButtonCopy onClick={() => copyByClipboard(pixTransaction?.qr_code)}>
                      Copiar código PIX
                    </Style.ButtonCopy>
                  )}

                  <Style.PaymentStatus>
                    Status do pagamento:{' '}
                    {paymentStatus === 'pending' ? 'Aguardando pagamento' : 'Processando pagamento'}
                  </Style.PaymentStatus>
                </motion.div>
              )}
            </AnimatePresence>
          ) : (
            <Style.ExpiredMessage>
              <p>O tempo para pagamento expirou. Por favor, gere um novo QR Code PIX.</p>
              <Style.Button onClick={handleFinishOrder} disabled={isLoading}>
                {isLoading ? 'Gerando QR Code...' : 'Gerar novo QR Code PIX'}
              </Style.Button>
            </Style.ExpiredMessage>
          )}
        </Style.CardContent>
      </Style.PixCard>
      <StatusModal
        onClickButton={() => {
          setError(false)
          setErrorDetails(null)
        }}
        variant="error"
        icon={<RiErrorWarningFill />}
        textButton="Tentar Novamente"
        isOpen={error}
        onClose={() => {
          setError(false)
          setErrorDetails(null)
        }}
        title="Houve um erro ao processar seu pagamento"
        text={
          errorDetails ? (
            <div className="error-group">
              <h4>Erro no PIX</h4>
              <div className="detail-item">
                <span className="label">Status:</span>
                <span className="value">{errorDetails.status}</span>
              </div>
              <div className="detail-item">
                <span className="label">Mensagem:</span>
                <span className="value">{errorDetails.acquirer_message}</span>
              </div>
            </div>
          ) : (
            'Por favor, revise seus dados e tente novamente'
          )
        }
      />
    </Style.Container>
  )
}
